import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { 
  FiTrendingUp,
  FiAward,
  FiCalendar,
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiPrinter
} from 'react-icons/fi';
import './AKMMahasiswa.css';

const AKMMahasiswa = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [currentAKM, setCurrentAKM] = useState(null);

  const fetchAKMList = useCallback(async () => {
    if (!selectedSemester) return;
    
    setLoading(true);
    try {
      const res = await axios.get('/api/akm', { 
        params: { 
          nim: user.nim,
          semester: selectedSemester
        } 
      });
      
      const data = res.data.data || [];
      
      if (data.length > 0) {
        setCurrentAKM(data[0]);
      } else {
        setCurrentAKM(null);
      }
      
    } catch (error) {
      console.error('Error fetching AKM:', error);
      if (error.response?.status === 404) {
        setCurrentAKM(null);
      } else {
        toast.error('Gagal memuat data AKM');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSemester, user.nim]);

  const fetchAvailableSemesters = useCallback(async () => {
    try {
      const res = await axios.get('/api/akm', { 
        params: { 
          nim: user.nim
        } 
      });
      
      const akmData = res.data.data || [];
      
      // Extract unique semesters
      const semesterMap = new Map();
      
      akmData.forEach(akm => {
        if (akm.id_semester && !semesterMap.has(akm.id_semester)) {
          semesterMap.set(akm.id_semester, {
            id_semester: akm.id_semester,
            nama_semester: akm.nama_semester || akm.id_semester
          });
        }
      });
      
      const semesters = Array.from(semesterMap.values())
        .sort((a, b) => b.id_semester.localeCompare(a.id_semester));
      
      setAvailableSemesters(semesters);
      if (semesters.length > 0) {
        setSelectedSemester(semesters[0].id_semester);
      }
    } catch (error) {
      console.error('Error fetching semesters:', error);
      toast.error('Gagal memuat daftar semester');
    }
  }, [user.nim]);

  useEffect(() => {
    fetchAvailableSemesters();
  }, [fetchAvailableSemesters]);

  useEffect(() => {
    if (selectedSemester) {
      fetchAKMList();
    }
  }, [selectedSemester, fetchAKMList]);

  const handlePrint = () => {
    if (!currentAKM) {
      toast.warning('Tidak ada data AKM untuk dicetak');
      return;
    }
    const printUrl = `http://localhost:5000/api/akm/print/${currentAKM._id}`;
    window.open(printUrl, '_blank');
  };

  const getSemesterName = (semesterId) => {
    const semester = availableSemesters.find(s => s.id_semester === semesterId);
    return semester?.nama_semester || semesterId;
  };

  const getIPKStatus = (ipk) => {
    if (ipk >= 3.5) return { text: 'Cum Laude', color: '#10b981', icon: FiAward };
    if (ipk >= 3.0) return { text: 'Sangat Baik', color: '#3b82f6', icon: FiCheckCircle };
    if (ipk >= 2.5) return { text: 'Baik', color: '#f59e0b', icon: FiCheckCircle };
    return { text: 'Cukup', color: '#ef4444', icon: FiAlertCircle };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (!currentAKM && !loading && selectedSemester) {
    return (
      <div className="akm-mahasiswa-container">
        <div className="akm-header">
          <div className="header-backdrop"></div>
          <div className="header-content">
            <div className="header-left">
              <div className="icon-wrapper">
                <FiAward className="header-icon" />
              </div>
              <div>
                <h1 className="header-title">Aktivitas Kuliah Mahasiswa (AKM)</h1>
                <p className="header-subtitle">Status dan prestasi akademik</p>
              </div>
            </div>
          </div>
        </div>

        <div className="semester-selector-card">
          <div className="selector-header">
            <FiCalendar className="selector-icon" />
            <h3>Pilih Semester</h3>
          </div>
          <div className="selector-content">
            <select 
              value={selectedSemester} 
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="semester-select"
            >
              <option value="">-- Pilih Semester --</option>
              {availableSemesters.map(sem => (
                <option key={sem.id_semester} value={sem.id_semester}>
                  {getSemesterName(sem.id_semester)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="empty-state-full">
          <FiInfo size={64} />
          <h3>Belum Ada Data AKM</h3>
          <p>Data AKM untuk semester ini belum tersedia</p>
        </div>
      </div>
    );
  }

  const ipkStatus = currentAKM ? getIPKStatus(currentAKM.ipk) : null;
  const StatusIcon = ipkStatus?.icon;

  return (
    <div className="akm-mahasiswa-container">
      {/* Header Section */}
      <div className="akm-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="icon-wrapper">
              <FiAward className="header-icon" />
            </div>
            <div>
              <h1 className="header-title">Aktivitas Kuliah Mahasiswa (AKM)</h1>
              <p className="header-subtitle">Status dan prestasi akademik</p>
            </div>
          </div>
          <div className="header-actions">
            {currentAKM && (
              <button className="btn-action btn-print" onClick={handlePrint}>
                <FiPrinter />
                <span>Cetak AKM</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Semester Selector */}
      <div className="semester-selector-card">
        <div className="selector-header">
          <FiCalendar className="selector-icon" />
          <h3>Pilih Semester</h3>
        </div>
        <div className="selector-content">
          <select 
            value={selectedSemester} 
            onChange={(e) => setSelectedSemester(e.target.value)}
            className="semester-select"
          >
            <option value="">-- Pilih Semester --</option>
            {availableSemesters.map(sem => (
              <option key={sem.id_semester} value={sem.id_semester}>
                {getSemesterName(sem.id_semester)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Memuat data AKM...</p>
        </div>
      ) : currentAKM && (
        <>
          {/* IPK Highlight Card */}
          <div className="ipk-highlight-card">
            <div className="ipk-background"></div>
            <div className="ipk-content">
              <div className="ipk-left">
                <div className="ipk-icon-wrapper">
                  {StatusIcon && <StatusIcon />}
                </div>
                <div className="ipk-details">
                  <p className="ipk-label">Indeks Prestasi Kumulatif</p>
                  <h1 className="ipk-value">{currentAKM.ipk?.toFixed(2) || '0.00'}</h1>
                  <span 
                    className="ipk-status-badge"
                    style={{ background: ipkStatus?.color }}
                  >
                    {ipkStatus?.text}
                  </span>
                </div>
              </div>
              <div className="ipk-right">
                <div className="ips-mini-card">
                  <p className="mini-label">IPS Semester Ini</p>
                  <h2 className="mini-value">{currentAKM.ips?.toFixed(2) || '0.00'}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="akm-stats-grid">
            <div className="akm-stat-card blue">
              <div className="stat-icon-circle">
                <FiCalendar />
              </div>
              <div className="stat-info">
                <p className="stat-title">Semester</p>
                <h3 className="stat-number">{getSemesterName(currentAKM.id_semester)}</h3>
              </div>
            </div>

            <div className="akm-stat-card green">
              <div className="stat-icon-circle">
                <FiTrendingUp />
              </div>
              <div className="stat-info">
                <p className="stat-title">SKS Semester</p>
                <h3 className="stat-number">{currentAKM.sks_semester || 0} SKS</h3>
              </div>
            </div>

            <div className="akm-stat-card purple">
              <div className="stat-icon-circle">
                <FiAward />
              </div>
              <div className="stat-info">
                <p className="stat-title">Total SKS</p>
                <h3 className="stat-number">{currentAKM.sks_total || 0} SKS</h3>
              </div>
            </div>

            <div className="akm-stat-card orange">
              <div className="stat-icon-circle">
                <FiDollarSign />
              </div>
              <div className="stat-info">
                <p className="stat-title">Biaya Kuliah</p>
                <h3 className="stat-number small">{formatCurrency(currentAKM.biaya_kuliah_smt)}</h3>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="details-card">
            <div className="card-header">
              <h3>
                <FiInfo />
                Informasi Akademik Detail
              </h3>
            </div>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Nama Status Mahasiswa</span>
                <span className="detail-value">
                  {currentAKM.nama_status_mahasiswa || '-'}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Program Studi</span>
                <span className="detail-value">
                  {currentAKM.nama_prodi || user.program || '-'}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">IPS (Indeks Prestasi Semester)</span>
                <span className="detail-value highlight">
                  {currentAKM.ips?.toFixed(2) || '0.00'}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">IPK (Indeks Prestasi Kumulatif)</span>
                <span className="detail-value highlight">
                  {currentAKM.ipk?.toFixed(2) || '0.00'}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">SKS Semester</span>
                <span className="detail-value">{currentAKM.sks_semester || 0} SKS</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">SKS Total</span>
                <span className="detail-value">{currentAKM.sks_total || 0} SKS</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Biaya Kuliah Semester</span>
                <span className="detail-value">
                  {formatCurrency(currentAKM.biaya_kuliah_smt)}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Status Aktivitas</span>
                <span className={`status-badge ${currentAKM.status === '1' ? 'active' : 'inactive'}`}>
                  {currentAKM.status === '1' ? 'Aktif' : 'Tidak Aktif'}
                </span>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <FiInfo />
            <div>
              <strong>Informasi:</strong>
              <p>Data AKM ini menunjukkan aktivitas dan prestasi akademik Anda per semester. Pastikan untuk selalu memantau IPK dan status akademik Anda.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AKMMahasiswa;
